import React, { useMemo, useState, useEffect } from 'react';
import axios, { AxiosError } from 'axios';
import { Input } from '../../../ui/form/Input';
import { Select, SelectItem } from '@tremor/react';
import { toast } from 'react-toastify';
import configManagement from '../../../network/configManagement';
import { useQuery, useQueryClient } from 'react-query';
import {
  CustomFieldDefinition,
  listCustomFields,
} from '../../../network/customFields';
import {
  useWorkspaceSettings,
  workspaceSettingsQueryKey,
} from './useWorkspaceSettings';

const API_HOST = process.env.REACT_APP_API_HOST;

// Helper to format a date as "D MMM,YYYY" e.g., "28 Oct,2024"
const formatDate = (date: Date): string => {
  const day = date.getDate();
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const month = months[date.getMonth()];
  const year = date.getFullYear();
  return `${day} ${month} ${year}`;
};

export function WorkSpaceDetails() {
  // Organization Information state
  const [companyName, setCompanyName] = useState('');
  const [industry, setIndustry] = useState('');
  const [employees, setEmployees] = useState('');

  // Absence Policies state
  const [sickLeave, setSickLeave] = useState('');
  const [pto, setPto] = useState('');
  const [parentalLeave, setParentalLeave] = useState('');
  const [wfh, setWfh] = useState('');
  const [bereavement, setBereavement] = useState('');
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  // Additional Organization Information state
  const [website, setWebsite] = useState('');
  const [timezone, setTimezone] = useState('');
  const [logoUrl, setLogoUrl] = useState('');
  const [country, setCountry] = useState('');
  const [attributeId, setAttributeId] = useState('');
  const [attributeResult, setAttributeResult] = useState<any>(null);
  const [isSyncing, setIsSyncing] = useState(false);
  const [companyCustomFields, setCompanyCustomFields] = useState<Record<string, any>>({});
  const queryClient = useQueryClient();

  const { data: companyFieldResponse } = useQuery({
    queryKey: ['custom-fields', 'company'],
    queryFn: () => listCustomFields({ scope: 'company', active: true }),
  });
  const companyFields: CustomFieldDefinition[] = useMemo(
    () => companyFieldResponse?.data || [],
    [companyFieldResponse?.data],
  );
  const { data: workspaceSettings, error: workspaceSettingsError } =
    useWorkspaceSettings();

  useEffect(() => {
    if (companyFields.length === 0) return;
    setCompanyCustomFields((prev) => {
      const next = { ...prev };
      companyFields.forEach((field) => {
        if (!(field.system.id in next)) {
          next[field.system.id] =
            field.details.type === 'multi_select' ? [] : '';
        }
      });
      return next;
    });
  }, [companyFields]);

  useEffect(() => {
    if (!workspaceSettings) return;

    const { general, timeoff, updatedAt } = workspaceSettings;

    setCompanyName(general?.companyName || '');
    setIndustry(general?.industry || '');
    setEmployees(general?.employees || '');
    setWebsite(general?.website || '');
    setTimezone(general?.timezone || general?.timeZone || '');
    setLogoUrl(general?.logoUrl || '');
    setCountry(general?.country || '');
    setCompanyCustomFields(general?.customFields || {});

    setSickLeave(String(timeoff?.sickLeave || '0'));
    setPto(String(timeoff?.pto || '0'));
    setParentalLeave(String(timeoff?.parentalLeave || '0'));
    setWfh(String(timeoff?.wfh || '0'));
    setBereavement(String(timeoff?.bereavement || '0'));
    setLastUpdated(updatedAt ? new Date(updatedAt) : new Date());
  }, [workspaceSettings]);

  useEffect(() => {
    if (!workspaceSettingsError) return;

    console.error(
      'An unexpected error occurred while fetching workspace details:',
      workspaceSettingsError,
    );
    toast.error('An unexpected error occurred while fetching data');
    setLastUpdated(new Date());
  }, [workspaceSettingsError]);

  const handleSaveChanges = async () => {
    const normalizedCustomFields = Object.fromEntries(
      Object.entries(companyCustomFields).map(([key, value]) => {
        if (Array.isArray(value)) {
          return [key, value.length === 0 ? null : value];
        }
        if (value === '') {
          return [key, null];
        }
        return [key, value];
      }),
    );

    const workspaceDetails = {
      companyName,
      industry,
      employees,
      website,
      timezone,
      logoUrl,
      country,
      sickLeave: parseInt(sickLeave, 10) || 0,
      pto: parseInt(pto, 10) || 0,
      parentalLeave: parseInt(parentalLeave, 10) || 0,
      wfh: parseInt(wfh, 10) || 0,
      bereavement: parseInt(bereavement, 10) || 0,
      customFields: normalizedCustomFields,
    };

    try {
      const response = await axios.patch(`${API_HOST}/v1/workspace`, workspaceDetails);
      if (response.status === 200) {
        await queryClient.invalidateQueries(workspaceSettingsQueryKey);
        toast.success('Workspace details updated successfully!');
      } else {
        toast.error(`Error updating workspace details: ${response.statusText}`);
      }
    } catch (error) {
      console.error("Error saving workspace details:", error);
      if (axios.isAxiosError(error)) {
        const axiosError = error as AxiosError;
        if (axiosError.response?.status === 400) {
          const errorData = axiosError.response.data as any;
          let message = 'Validation failed. Please check your inputs.';
          if (errorData && errorData.message) {
            if (Array.isArray(errorData.message)) {
              message = errorData.message.join('; ');
            } else {
              message = String(errorData.message);
            }
          }
          toast.error(`Update failed: ${message}`);
        } else {
          toast.error(`An unexpected error occurred: ${axiosError.message}`);
        }
      } else {
        toast.error('An unexpected error occurred.');
      }
    }
  };

  const handleSyncConfig = async () => {
    try {
      setIsSyncing(true);
      await configManagement.syncConfig();
      toast.success('Config sync completed.');
    } catch (error) {
      toast.error('Config sync failed.');
    } finally {
      setIsSyncing(false);
    }
  };

  const handleFetchAttribute = async () => {
    if (!attributeId) {
      toast.error('Provide an attribute ID to fetch.');
      return;
    }
    try {
      const result = await configManagement.getAttribute(attributeId);
      setAttributeResult(result?.data || result);
    } catch (error) {
      toast.error('Failed to fetch attribute.');
    }
  };

  const handleCompanyCustomFieldChange = (fieldId: string, value: any) => {
    setCompanyCustomFields((prev) => ({
      ...prev,
      [fieldId]: value,
    }));
  };

  return (
    <div className="flex flex-col gap-5">
      <div className="items-ceneter mt-1 flex flex-row justify-between">
        <p className="rounded-lg bg-[#F4F4F5] px-3 py-1">
          Last update: {lastUpdated ? formatDate(lastUpdated) : 'Loading...'}
        </p>
      </div>
      <div className="flex flex-row rounded-xl p-5 shadow">
        <div className="flex w-[50%] flex-col">
          <h5 className="text-base font-medium">Organization Information</h5>
          <p className="text-gray-500">Enter details about your organization</p>
        </div>
        <div className="flex w-[50%] flex-col">
          <div>
            <label
              htmlFor="company_name"
              className="mb-1 block text-base font-medium"
            >
              Company name
            </label>
            <Input
              type="text"
              placeholder="Company name"
              id="company_name"
              name="company_name"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
            />
          </div>
          <div className="mt-2">
            <label
              htmlFor="industry"
              className="mb-1 block text-base font-medium"
            >
              Industry
            </label>
            <Select
              id="industry"
              name="industry"
              className="w-full"
              value={industry}
              onValueChange={(value) => setIndustry(value)}
            >
              <SelectItem value="Technology">Technology</SelectItem>
              <SelectItem value="Healthcare">Healthcare</SelectItem>
              <SelectItem value="Finance">Finance</SelectItem>
              <SelectItem value="Manufacturing">Manufacturing</SelectItem>
              <SelectItem value="Retail">Retail</SelectItem>
              <SelectItem value="Education">Education</SelectItem>
              <SelectItem value="Other">Other</SelectItem>
            </Select>
          </div>
          <div className="mt-2">
            <label
              htmlFor="employees"
              className="mb-1 block text-base font-medium"
            >
              Number of Employees
            </label>
            <Select
              id="employees"
              name="employees"
              className="w-full"
              value={employees}
              onValueChange={(value) => setEmployees(value)}
            >
              <SelectItem value="1-50">1-50</SelectItem>
              <SelectItem value="51-200">51-200</SelectItem>
              <SelectItem value="201-500">201-500</SelectItem>
              <SelectItem value="500+">500+</SelectItem>
            </Select>
          </div>
          <div className="mt-2">
            <label
              htmlFor="website"
              className="mb-1 block text-base font-medium"
            >
              Website
            </label>
            <Input
              type="url"
              placeholder="https://example.com"
              id="website"
              name="website"
              value={website}
              onChange={(e) => setWebsite(e.target.value)}
            />
          </div>
          <div className="mt-2">
            <label
              htmlFor="logo_url"
              className="mb-1 block text-base font-medium"
            >
              Logo URL
            </label>
            <Input
              type="url"
              placeholder="https://example.com/logo.png"
              id="logo_url"
              name="logo_url"
              value={logoUrl}
              onChange={(e) => setLogoUrl(e.target.value)}
            />
          </div>
          <div className="mt-2">
            <label
              htmlFor="country"
              className="mb-1 block text-base font-medium"
            >
              Country
            </label>
            <Select
              id="country"
              name="country"
              className="w-full"
              value={country}
              onValueChange={(value) => setCountry(value)}
            >
              <SelectItem value="USA">United States</SelectItem>
              <SelectItem value="UK">United Kingdom</SelectItem>
              <SelectItem value="Canada">Canada</SelectItem>
              <SelectItem value="Australia">Australia</SelectItem>
              <SelectItem value="Germany">Germany</SelectItem>
              <SelectItem value="France">France</SelectItem>
              <SelectItem value="Japan">Japan</SelectItem>
              <SelectItem value="China">China</SelectItem>
              <SelectItem value="India">India</SelectItem>
              <SelectItem value="Other">Other</SelectItem>
            </Select>
          </div>
          <div className="mt-2">
            <label
              htmlFor="timezone"
              className="mb-1 block text-base font-medium"
            >
              Timezone
            </label>
            <Select
              id="timezone"
              name="timezone"
              className="w-full"
              value={timezone}
              onValueChange={(value) => setTimezone(value)}
            >
              <SelectItem value="UTC-12:00">UTC-12:00</SelectItem>
              <SelectItem value="UTC-11:00">UTC-11:00</SelectItem>
              <SelectItem value="UTC-10:00">UTC-10:00 (HST)</SelectItem>
              <SelectItem value="UTC-09:00">UTC-09:00 (AKST)</SelectItem>
              <SelectItem value="UTC-08:00">UTC-08:00 (PST)</SelectItem>
              <SelectItem value="UTC-07:00">UTC-07:00 (MST)</SelectItem>
              <SelectItem value="UTC-06:00">UTC-06:00 (CST)</SelectItem>
              <SelectItem value="UTC-05:00">UTC-05:00 (EST)</SelectItem>
              <SelectItem value="UTC-04:00">UTC-04:00 (AST)</SelectItem>
              <SelectItem value="UTC-03:00">UTC-03:00</SelectItem>
              <SelectItem value="UTC-02:00">UTC-02:00</SelectItem>
              <SelectItem value="UTC-01:00">UTC-01:00</SelectItem>
              <SelectItem value="UTC+00:00">UTC+00:00 (GMT)</SelectItem>
              <SelectItem value="UTC+01:00">UTC+01:00 (CET)</SelectItem>
              <SelectItem value="UTC+02:00">UTC+02:00 (EET)</SelectItem>
              <SelectItem value="UTC+03:00">UTC+03:00 (MSK)</SelectItem>
              <SelectItem value="UTC+04:00">UTC+04:00</SelectItem>
              <SelectItem value="UTC+05:00">UTC+05:00</SelectItem>
              <SelectItem value="UTC+05:30">UTC+05:30 (IST)</SelectItem>
              <SelectItem value="UTC+06:00">UTC+06:00</SelectItem>
              <SelectItem value="UTC+07:00">UTC+07:00</SelectItem>
              <SelectItem value="UTC+08:00">UTC+08:00 (CST)</SelectItem>
              <SelectItem value="UTC+09:00">UTC+09:00 (JST)</SelectItem>
              <SelectItem value="UTC+10:00">UTC+10:00 (AEST)</SelectItem>
              <SelectItem value="UTC+11:00">UTC+11:00</SelectItem>
              <SelectItem value="UTC+12:00">UTC+12:00</SelectItem>
            </Select>
          </div>
        </div>
      </div>

      {companyFields.length > 0 && (
        <div className="flex flex-col gap-4 rounded-xl p-5 shadow">
          <div>
            <h5 className="text-base font-medium">Company custom fields</h5>
            <p className="text-gray-500">
              Capture additional details for your workspace.
            </p>
          </div>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {companyFields.map((field) => {
              const value = companyCustomFields[field.system.id];

              if (field.details.type === 'boolean') {
                return (
                  <label
                    key={field.system.id}
                    className="flex items-center gap-3 text-sm text-gray-700"
                  >
                    <input
                      type="checkbox"
                      checked={Boolean(value)}
                      onChange={(e) =>
                        handleCompanyCustomFieldChange(
                          field.system.id,
                          e.target.checked,
                        )
                      }
                    />
                    {field.details.name}
                  </label>
                );
              }

              if (field.details.type === 'single_select') {
                return (
                  <div key={field.system.id}>
                    <label className="mb-1 block text-base font-medium">
                      {field.details.name}
                    </label>
                    <Select
                      value={value || ''}
                      onValueChange={(val) =>
                        handleCompanyCustomFieldChange(field.system.id, val)
                      }
                    >
                      <SelectItem value="">Select...</SelectItem>
                      {(field.details.options || []).map((option) => (
                        <SelectItem key={option} value={option}>
                          {option}
                        </SelectItem>
                      ))}
                    </Select>
                  </div>
                );
              }

              if (field.details.type === 'multi_select') {
                const selected = Array.isArray(value)
                  ? value
                  : typeof value === 'string' && value
                    ? [value]
                    : [];
                return (
                  <div key={field.system.id}>
                    <label className="mb-1 block text-base font-medium">
                      {field.details.name}
                    </label>
                    <select
                      className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                      multiple
                      value={selected}
                      onChange={(e) =>
                        handleCompanyCustomFieldChange(
                          field.system.id,
                          Array.from(e.target.selectedOptions).map((o) => o.value),
                        )
                      }
                    >
                      {(field.details.options || []).map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  </div>
                );
              }

              if (field.details.type === 'date') {
                return (
                  <div key={field.system.id}>
                    <label className="mb-1 block text-base font-medium">
                      {field.details.name}
                    </label>
                    <Input
                      type="date"
                      value={value || ''}
                      onChange={(e) =>
                        handleCompanyCustomFieldChange(
                          field.system.id,
                          e.target.value,
                        )
                      }
                    />
                  </div>
                );
              }

              if (field.details.type === 'number') {
                return (
                  <div key={field.system.id}>
                    <label className="mb-1 block text-base font-medium">
                      {field.details.name}
                    </label>
                    <Input
                      type="number"
                      value={value ?? ''}
                      onChange={(e) =>
                        handleCompanyCustomFieldChange(
                          field.system.id,
                          e.target.value,
                        )
                      }
                    />
                  </div>
                );
              }

              return (
                <div key={field.system.id}>
                  <label className="mb-1 block text-base font-medium">
                    {field.details.name}
                  </label>
                  <Input
                    type="text"
                    value={value || ''}
                    onChange={(e) =>
                      handleCompanyCustomFieldChange(
                        field.system.id,
                        e.target.value,
                      )
                    }
                  />
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="flex flex-col gap-4 rounded-xl p-5 shadow">
        <h5 className="text-base font-medium">Config Utilities</h5>
        <p className="text-gray-500">
          Use these tools to sync configuration and resolve attributes.
        </p>
        <div className="flex flex-row items-center gap-3">
          <button
            type="button"
            onClick={handleSyncConfig}
            className="rounded-md bg-[#475467] px-4 py-2 text-sm font-semibold text-white"
            disabled={isSyncing}
          >
            {isSyncing ? 'Syncing...' : 'Sync Config'}
          </button>
        </div>
        <div className="flex flex-col gap-2">
          <label htmlFor="attributeId" className="text-sm font-medium">
            Attribute ID
          </label>
          <div className="flex flex-row items-center gap-3">
            <Input
              id="attributeId"
              name="attributeId"
              value={attributeId}
              onChange={(e) => setAttributeId(e.target.value)}
              placeholder="Enter attribute ID"
            />
            <button
              type="button"
              onClick={handleFetchAttribute}
              className="rounded-md border px-4 py-2 text-sm font-medium"
            >
              Fetch
            </button>
          </div>
          {attributeResult && (
            <pre className="rounded-md bg-gray-50 p-3 text-xs text-gray-700">
              {JSON.stringify(attributeResult, null, 2)}
            </pre>
          )}
        </div>
      </div>
      <div className="flex flex-row rounded-xl p-5 shadow">
        <div className="flex w-[50%] flex-col">
          <h5 className="text-base font-medium">Absence Policies</h5>
          <p className="text-gray-500">
            Define and manage your company's absence policies
          </p>
        </div>
        <div className="flex w-[50%] flex-col">
          <div>
            <label
              htmlFor="sick_leave"
              className="mb-1 block text-base font-medium"
            >
              Sick Leave
            </label>
            <Input
              type="number"
              placeholder="0"
              id="sick_leave"
              name="sick_leave"
              value={sickLeave}
              onChange={(e) => setSickLeave(e.target.value)}
            />
          </div>
          <div className="mt-2">
            <label htmlFor="pto" className="mb-1 block text-base font-medium">
              Paid Time Off (PTO)
            </label>
            <Input
              type="number"
              placeholder="0"
              id="pto"
              name="pto"
              value={pto}
              onChange={(e) => setPto(e.target.value)}
            />
          </div>
          <div className="mt-2">
            <label
              htmlFor="parental_leave"
              className="mb-1 block text-base font-medium"
            >
              Parental Leave
            </label>
            <Input
              type="number"
              placeholder="0"
              id="parental_leave"
              name="parental_leave"
              value={parentalLeave}
              onChange={(e) => setParentalLeave(e.target.value)}
            />
          </div>
          <div className="mt-2">
            <label
              htmlFor="wfh"
              className="mb-1 block text-base font-medium"
            >
              Work From Home (WFH) Days
            </label>
            <Input
              type="number"
              placeholder="0"
              id="wfh"
              name="wfh"
              value={wfh}
              onChange={(e) => setWfh(e.target.value)}
            />
          </div>
          <div className="mt-2">
            <label
              htmlFor="bereavement"
              className="mb-1 block text-base font-medium"
            >
              Bereavement Days
            </label>
            <Input
              type="number"
              placeholder="0"
              id="bereavement"
              name="bereavement"
              value={bereavement}
              onChange={(e) => setBereavement(e.target.value)}
            />
          </div>
        </div>
      </div>
      <div className="flex flex-row justify-end gap-4">
        <button className="flex items-center rounded-lg border-[1px] px-3 py-1 font-normal shadow">
          Go Back
        </button>
        <button
          className="flex items-center rounded-lg bg-[#DC2626] px-3 py-1 font-normal text-white"
          onClick={handleSaveChanges}
        >
          Save changes
        </button>
      </div>
    </div>
  );
}
